import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import Modal from '../Modal';
import { RootState } from '../../store';
import { currentVideoSrc } from '../../store/translation/selectors';
import { setShowVideoFeedbackModal } from '../../store/interface';
import { getCFID } from '../../utils/videoHelper';
import submitVideoFeedback, {
  OverallFeedbackOption,
  VideoFeedbackSubmitPayload,
} from '../../utils/api/createUserVideoFeedback';
import styles from './styles.module.css';

type ScoreValue = 1 | 2 | 3 | 4 | 5;

type RequiredScoreFields = Pick<
  VideoFeedbackSubmitPayload,
  'cana_acceptable' | 'cana_accuracy' | 'cana_clarity' | 'cana_natural' | 'cana_overall'
>;

type ScoreFields = Record<keyof RequiredScoreFields, ScoreValue | ''>;

const ratingOptions: ScoreValue[] = [1, 2, 3, 4, 5];

const overallFeedbackOptions: OverallFeedbackOption[] = [
  'Good',
  'Not Good',
  'Needs Improvement',
  "Don't Understand",
  'Excellent',
  'Other',
];

const initialScores: ScoreFields = {
  cana_acceptable: '',
  cana_accuracy: '',
  cana_clarity: '',
  cana_natural: '',
  cana_overall: '',
};

const scoreFieldLabels: Record<keyof RequiredScoreFields, string> = {
  cana_acceptable: 'Acceptable',
  cana_accuracy: 'Accuracy',
  cana_clarity: 'Clarity',
  cana_natural: 'Natural',
  cana_overall: 'Overall',
};

const VideoFeedbackModal = (): JSX.Element => {
  const dispatch = useDispatch();
  const { showVideoFeedbackModal } = useSelector((state: RootState) => state.interface);
  const { darkMode } = useSelector((state: RootState) => state.settings);
  const selectedVideoSrc = useSelector(currentVideoSrc);

  const [scores, setScores] = useState<ScoreFields>(initialScores);
  const [overallFeedback, setOverallFeedback] = useState<OverallFeedbackOption | ''>('');
  const [comments, setComments] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState<'' | 'yes' | 'no'>('');
  const [inProgress, setInProgress] = useState(false);
  const [mode, setMode] = useState<'form' | 'success'>('form');
  const [errorMessage, setErrorMessage] = useState('');

  const resetForm = () => {
    setScores(initialScores);
    setOverallFeedback('');
    setComments('');
    setWouldRecommend('');
    setErrorMessage('');
    setMode('form');
  };

  const closeSelf = () => {
    if (inProgress) {
      return;
    }
    dispatch(setShowVideoFeedbackModal(false));
    setTimeout(resetForm, 300);
  };

  const getVideoId = (): string | undefined => {
    const hlsSource = selectedVideoSrc?.src.hls;
    if (hlsSource === undefined) {
      return undefined;
    }

    try {
      return getCFID(hlsSource);
    } catch (error) {
      return undefined;
    }
  };

  const submitFeedback = async () => {
    if (inProgress) {
      return;
    }

    const hasMissingScore = (Object.keys(scores) as (keyof ScoreFields)[]).some(
      fieldName => scores[fieldName] === ''
    );

    if (hasMissingScore) {
      setErrorMessage('Please select a score (1-5) for each category.');
      return;
    }

    if (overallFeedback === '') {
      setErrorMessage('Please select an overall feedback option.');
      return;
    }

    const videoId = getVideoId();
    if (videoId === undefined) {
      setErrorMessage('Unable to submit feedback because the current video id is unavailable.');
      return;
    }

    setInProgress(true);
    setErrorMessage('');

    const requiredScores = scores as RequiredScoreFields;

    const payload: VideoFeedbackSubmitPayload = {
      video_id: videoId,
      ...requiredScores,
      overall_feedback: overallFeedback,
      feedback_comments: comments.trim() || undefined,
      would_recommend: wouldRecommend === '' ? undefined : wouldRecommend === 'yes',
    };

    try {
      await submitVideoFeedback(payload);
      resetForm();
      setMode('success');
    } catch (error) {
      setErrorMessage('Unable to send feedback right now. Please try again.');
    } finally {
      setInProgress(false);
    }
  };

  let modalBody: JSX.Element;

  if (inProgress) {
    modalBody = (
      <div className={styles.loadingContainer}>
        <PulseLoader size={10} color={darkMode ? '#E8EEF2' : '#161616'} />
      </div>
    );
  } else if (mode === 'success') {
    modalBody = (
      <div className={styles.thankYouContainer}>
        <div className={styles.thankYouTitle}>Thank you for your feedback.</div>
        <button type="button" className={styles.submitButton} onClick={closeSelf}>
          Close
        </button>
      </div>
    );
  } else {
    modalBody = (
      <>
        <div className={styles.description}>
          Share your feedback about the current video. Ratings are from 1 (lowest) to 5 (highest).
        </div>

        <div className={styles.grid}>
          {(Object.keys(scoreFieldLabels) as (keyof ScoreFields)[]).map(fieldName => {
            const scoreGroupName = `video-feedback-${fieldName}`;

            return (
              <fieldset key={fieldName} className={styles.scoreFieldset}>
                <legend className={styles.scoreLegend}>
                  <span className={styles.requiredAsterisk}>*</span>
                  {scoreFieldLabels[fieldName]}
                </legend>
                <div className={styles.radioRow}>
                  {ratingOptions.map(option => {
                    const scoreInputId = `${scoreGroupName}-${option}`;

                    return (
                      <label
                        key={scoreInputId}
                        htmlFor={scoreInputId}
                        className={styles.radioLabel}
                      >
                        <input
                          id={scoreInputId}
                          type="radio"
                          name={scoreGroupName}
                          value={option}
                          checked={scores[fieldName] === option}
                          onChange={() => setScores(prev => ({ ...prev, [fieldName]: option }))}
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>

        <label className={styles.fieldLabel} htmlFor="video-feedback-overall-feedback">
          <span>
            <span className={styles.requiredAsterisk}>*</span>
            Overall Feedback
          </span>
          <select
            id="video-feedback-overall-feedback"
            value={overallFeedback}
            className={styles.select}
            onChange={event => setOverallFeedback(event.target.value as OverallFeedbackOption | '')}
          >
            <option value="">Select one</option>
            {overallFeedbackOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.fieldLabel} htmlFor="video-feedback-recommend">
          <span>Would you recommend this video?</span>
          <select
            id="video-feedback-recommend"
            value={wouldRecommend}
            className={styles.select}
            onChange={event => setWouldRecommend(event.target.value as '' | 'yes' | 'no')}
          >
            <option value="">Select one</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>

        <label className={styles.fieldLabel} htmlFor="video-feedback-comments">
          <span>Comments</span>
          <textarea
            id="video-feedback-comments"
            value={comments}
            className={styles.textarea}
            placeholder="Optional comments"
            rows={4}
            onChange={event => setComments(event.target.value)}
          />
        </label>

        {errorMessage !== '' && <div className={styles.errorMessage}>{errorMessage}</div>}

        <button type="button" className={styles.submitButton} onClick={submitFeedback}>
          Submit Feedback
        </button>
      </>
    );
  }

  return (
    <Modal
      show={showVideoFeedbackModal}
      closeSelf={closeSelf}
      title={mode === 'success' ? 'Feedback Submitted' : 'Video Feedback'}
      ariaLabel="Close the video feedback modal"
      darkerBackground
      closeOnExternalClick={false}
    >
      <div className={`${styles.container} ${!darkMode && styles.lightMode}`}>{modalBody}</div>
    </Modal>
  );
};

export default VideoFeedbackModal;
